import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { errorFromResponse } from '@/lib/errors';
import { idempotencyKeyFor } from '@/lib/http-core';
import type {
  Notice,
  NoticeListQuery,
  NoticeListResult,
  NoticePagination,
  NoticePayload,
  NoticeReorderPayload,
} from '../types/notice';

const BASE = '/admin/v1/cms/notices';

interface ListEnvelope {
  success: boolean;
  data?: Notice[] | null;
  meta?: { pagination?: NoticePagination } | null;
}

interface SingleEnvelope {
  success: boolean;
  data?: Notice | null;
}

export const noticesQueryKey = (query: NoticeListQuery) =>
  ['admin', 'notices', query.page, query.size, query.status ?? '', query.category ?? ''] as const;

export const noticeQueryKey = (noticeId: number) => ['admin', 'notice', noticeId] as const;

// 정렬은 BE 가 정본으로 소유한다: admin list API 가 모든 분기에서
// isPinned DESC, pinOrder ASC, publishedAt DESC 로 내려준다(be-catalog ID-13 후속).
// 따라서 명시 sort 파라미터를 보내지 않고(클라가 BE 순서를 덮지 않도록) API 순서를 그대로 렌더한다.
const buildSearchParams = (query: NoticeListQuery): string => {
  const params = new URLSearchParams({
    page: String(Math.max(0, query.page - 1)),
    size: String(query.size),
  });
  if (query.status) params.set('status', query.status);
  if (query.category && query.category.trim()) params.set('category', query.category.trim());
  return params.toString();
};

const fetchNotices = async (query: NoticeListQuery): Promise<NoticeListResult> => {
  const res = await adminFetch(`${BASE}?${buildSearchParams(query)}`);
  if (!res.ok) throw new Error(`notices list failed: ${res.status}`);
  const env = (await res.json()) as ListEnvelope;
  // API 가 준 순서를 그대로 사용(클라이언트 재정렬 없음 — be-catalog 가 reorder 결과 정렬을 BE 에서 보장).
  const items = env.data ?? [];
  const pagination: NoticePagination = env.meta?.pagination ?? {
    page: query.page,
    size: query.size,
    totalItems: items.length,
    totalPages: 1,
  };
  return { items, pagination };
};

const fetchNotice = async (noticeId: number): Promise<Notice> => {
  const res = await adminFetch(`${BASE}/${noticeId}`);
  if (!res.ok) throw new Error(`notice fetch failed: ${res.status}`);
  const env = (await res.json()) as SingleEnvelope;
  if (!env.data) throw new Error('no data');
  return env.data;
};

export const useNoticesQuery = (query: NoticeListQuery) =>
  useQuery<NoticeListResult>({
    queryKey: noticesQueryKey(query),
    queryFn: () => fetchNotices(query),
  });

export const useNoticeQuery = (noticeId: number | null) =>
  useQuery<Notice>({
    queryKey: noticeQueryKey(noticeId ?? -1),
    queryFn: () => fetchNotice(noticeId as number),
    enabled: noticeId != null,
  });

export const useNoticeMutation = () => {
  const qc = useQueryClient();
  const invalidateList = () => qc.invalidateQueries({ queryKey: ['admin', 'notices'] });

  const create = useMutation({
    mutationFn: async (payload: NoticePayload): Promise<Notice> => {
      const res = await adminFetch(BASE, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', BASE, payload) },
        body: JSON.stringify(payload),
      });
      // 저장 실패는 BE error.code·message 를 살려 던진다(예: base64 이미지 400 사유를 그대로 안내).
      if (!res.ok) throw await errorFromResponse(res);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: invalidateList,
  });

  const update = useMutation({
    mutationFn: async ({
      noticeId,
      payload,
    }: {
      noticeId: number;
      payload: NoticePayload;
    }): Promise<Notice> => {
      const path = `${BASE}/${noticeId}`;
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', path, payload) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw await errorFromResponse(res);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: (_, { noticeId }) => {
      void qc.invalidateQueries({ queryKey: noticeQueryKey(noticeId) });
      invalidateList();
    },
  });

  const remove = useMutation({
    mutationFn: async (noticeId: number): Promise<void> => {
      const path = `${BASE}/${noticeId}`;
      const res = await adminFetch(path, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': idempotencyKeyFor('DELETE', path, '') },
      });
      if (!res.ok) throw new Error(`notice delete failed: ${res.status}`);
    },
    onSuccess: invalidateList,
  });

  const publish = useMutation({
    mutationFn: async (noticeId: number): Promise<Notice> => {
      const path = `${BASE}/${noticeId}/publish`;
      const res = await adminFetch(path, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', path, '') },
      });
      if (!res.ok) throw new Error(`notice publish failed: ${res.status}`);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: (notice) => {
      void qc.invalidateQueries({ queryKey: noticeQueryKey(notice.noticeId) });
      invalidateList();
    },
  });

  // 고정 공지 일괄 재정렬(be-catalog 계약): PUT /admin/v1/cms/notices/reorder
  // body { orderedNoticeIds: [...] } — 배열 인덱스가 곧 pinOrder. 응답 envelope data:null.
  const reorder = useMutation({
    mutationFn: async (orderedNoticeIds: number[]): Promise<void> => {
      const path = `${BASE}/reorder`;
      const body: NoticeReorderPayload = { orderedNoticeIds };
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', path, body) },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`notice reorder failed: ${res.status}`);
    },
    onSuccess: invalidateList,
  });

  return { create, update, remove, publish, reorder };
};
